import type { BoardSummaryFilterDraftRow } from './taskTrackerBoardSummaryStickerFilters.utils'
import type {
  TaskTrackerSummaryColumnsProjectConfig,
  TaskTrackerSummaryRule,
} from './taskTrackerBoardSummary.types'

export type BoardSummarySourceDraft = {
  id: string
  projectId: number | null
  boardId: number | null
  columnIds: number[]
}

export type BoardSummaryRuleDraft = {
  id: string
  sources: BoardSummarySourceDraft[]
  filterRows: BoardSummaryFilterDraftRow[]
  filtersHydrated: boolean
}

let boardSummaryRuleIdCounter = 0
let boardSummarySourceIdCounter = 0

export const createBoardSummaryRuleId = () => {
  boardSummaryRuleIdCounter += 1
  return `board-summary-rule-${boardSummaryRuleIdCounter}`
}

export const createBoardSummarySourceId = () => {
  boardSummarySourceIdCounter += 1
  return `board-summary-source-${boardSummarySourceIdCounter}`
}

export const createEmptyBoardSummarySource = (
  projectId: number | null = null,
  boardId: number | null = null,
): BoardSummarySourceDraft => ({
  id: createBoardSummarySourceId(),
  projectId,
  boardId,
  columnIds: [],
})

export const createEmptyBoardSummaryRule = (
  projectId: number | null = null,
  boardId: number | null = null,
): BoardSummaryRuleDraft => ({
  id: createBoardSummaryRuleId(),
  sources: [createEmptyBoardSummarySource(projectId, boardId)],
  filterRows: [],
  filtersHydrated: true,
})

export const mapColumnsConfigToRuleSources = (
  columnsConfig: TaskTrackerSummaryColumnsProjectConfig[],
): BoardSummarySourceDraft[] => {
  if (!columnsConfig.some((project) => project.boards?.length)) {
    return [createEmptyBoardSummarySource()]
  }

  return columnsConfig.flatMap((project) =>
    project.boards.map((board) => ({
      id: createBoardSummarySourceId(),
      projectId: project.projectId,
      boardId: board.boardId,
      columnIds: [...new Set(board.columnIds ?? [])],
    })),
  )
}

export const mapRulesConfigToDraftRules = (
  rulesConfig: TaskTrackerSummaryRule[],
): BoardSummaryRuleDraft[] => {
  if (!rulesConfig.length) {
    return [createEmptyBoardSummaryRule()]
  }

  return rulesConfig.map((rule) => ({
    id: createBoardSummaryRuleId(),
    sources: mapColumnsConfigToRuleSources(rule.columnsConfig),
    filterRows: [],
    filtersHydrated: false,
  }))
}

export const buildRuleColumnsConfig = (
  sources: BoardSummarySourceDraft[],
): TaskTrackerSummaryColumnsProjectConfig[] => {
  const projects = new Map<number, TaskTrackerSummaryColumnsProjectConfig>()

  sources.forEach((source) => {
    if (
      source.projectId == null ||
      source.projectId <= 0 ||
      source.boardId == null ||
      source.boardId <= 0 ||
      source.columnIds.length === 0
    ) {
      return
    }

    const project =
      projects.get(source.projectId) ??
      ({
        projectId: source.projectId,
        boards: [],
      } satisfies TaskTrackerSummaryColumnsProjectConfig)

    project.boards.push({
      boardId: Number(source.boardId),
      columnIds: [...new Set(source.columnIds.filter((columnId) => columnId > 0))],
    })
    projects.set(source.projectId, project)
  })

  return Array.from(projects.values()).filter((project) => project.boards.length > 0)
}

export const resolveRulePrimaryBoardId = (
  rule: BoardSummaryRuleDraft,
  fallbackBoardId: number | null = null,
): number | null => {
  const sourceWithColumns = rule.sources.find(
    (source) => source.boardId != null && source.columnIds.length > 0,
  )
  if (sourceWithColumns?.boardId != null) {
    return sourceWithColumns.boardId
  }

  const sourceWithBoard = rule.sources.find((source) => source.boardId != null)
  if (sourceWithBoard?.boardId != null) {
    return sourceWithBoard.boardId
  }

  return fallbackBoardId
}

export const isBoardSummaryRuleDraftValid = (
  rule: BoardSummaryRuleDraft,
  isBoardAccessible: (boardId: number) => boolean = () => true,
) =>
  rule.sources.some(
    (source) =>
      source.boardId != null &&
      source.boardId > 0 &&
      source.projectId != null &&
      source.projectId > 0 &&
      source.columnIds.length > 0 &&
      isBoardAccessible(source.boardId),
  )

export const collectBoardSummaryRuleBoardIds = (
  rules: BoardSummaryRuleDraft[],
): number[] => [
  ...new Set(
    rules
      .flatMap((rule) => rule.sources.map((source) => source.boardId))
      .filter((boardId): boardId is number => boardId != null && boardId > 0),
  ),
]
